"use client";

import { useState, useRef, useEffect } from "react";
import { motion } from "framer-motion";
import { Send, CheckCircle, AlertCircle, Shield, Clock, Facebook, Instagram } from "lucide-react";
import { fadeInUp } from "@/utils/animations";
import { useLanguage } from "@/contexts/LanguageContext";

// Google Form configuration
const GOOGLE_FORM_ACTION = "https://docs.google.com/forms/u/0/d/e/1FAIpQLSf1GNkh_YJnOmr_s00GzYCnsCuBdA0qap8JbqKEZtN5L2S77Q/formResponse";
const GOOGLE_FORM_FIELDS = {
  name: "entry.2005620554",
  email: "entry.1045781291",
  brief: "entry.839337160"
} as const;

// Reverse mapping from Google Form field IDs to state keys
const FIELD_ID_TO_STATE_KEY: Record<string, keyof FormData> = {
  [GOOGLE_FORM_FIELDS.name]: "name",
  [GOOGLE_FORM_FIELDS.email]: "email", 
  [GOOGLE_FORM_FIELDS.brief]: "brief",
};

type FormData = {
  name: string;
  email: string;
  brief: string;
};

type FormErrors = Partial<Record<keyof FormData, string>>;

const Contact = () => {
  const [formData, setFormData] = useState<FormData>({
    name: "",
    email: "",
    brief: "",
  });
  
  const [errors, setErrors] = useState<FormErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const [spamError, setSpamError] = useState<string | null>(null);
  const [honeypot, setHoneypot] = useState("");
  
  const { t } = useLanguage();
  const formRef = useRef<HTMLFormElement>(null);
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const mountTimeRef = useRef<number>(Date.now());
  const submittedRef = useRef<boolean>(false);

  // Clean up ref on unmount
  useEffect(() => {
    return () => {
      submittedRef.current = false;
    };
  }, []);

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = "Name is required";
    }

    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = "Please enter a valid email address";
    }

    if (!formData.brief.trim()) {
      newErrors.brief = "Please provide a brief description";
    } else if (formData.brief.trim().length < 20) {
      newErrors.brief = "Please provide more details (minimum 20 characters)";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const checkSpamProtections = (): boolean => {
    setSpamError(null);
    
    // 1. Check honeypot field
    if (honeypot.trim() !== "") {
      setSpamError("Submission blocked by spam filter.");
      return false;
    }
    
    // 2. Check minimum submission time (5 seconds)
    const elapsedTime = Date.now() - mountTimeRef.current;
    const MIN_SUBMISSION_TIME = 5000; // 5 seconds
    
    if (elapsedTime < MIN_SUBMISSION_TIME) {
      setSpamError("Please spend a few more moments reviewing the form before submitting.");
      return false;
    }
    
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      console.error("Form validation failed");
      return;
    }

    if (!checkSpamProtections()) {
      return;
    }

    setIsSubmitting(true);
    setSpamError(null);

    try {
      // Set ref flag for iframe detection
      submittedRef.current = true;
      
      // Programmatically submit the form to the hidden iframe
      if (formRef.current) {
        formRef.current.submit();
      }
      
      // Show success state (will be confirmed by iframe onLoad)
      setIsSuccess(true);
      
      // Reset form
      setFormData({
        name: "",
        email: "",
        brief: "",
      });
      setErrors({});
      setHoneypot("");
      
      // Reset success state after 8 seconds if iframe doesn't trigger
      const resetTimeout = setTimeout(() => {
        if (submittedRef.current) {
          setIsSuccess(false);
        }
      }, 8000);
      
      return () => clearTimeout(resetTimeout);
    } catch (error) {
      console.error("Form submission failed:", error);
      setSpamError("Submission failed. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleIframeLoad = () => {
    if (submittedRef.current && iframeRef.current) {
      // Form was submitted to Google Forms successfully
      setIsSuccess(true);
      submittedRef.current = false;
      
      // Hide success after 5 seconds
      setTimeout(() => {
        setIsSuccess(false);
      }, 5000);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    
    // Don't update honeypot field from user input (it's hidden)
    if (name === "_gotcha") {
      return;
    }
    
    // Map Google Form field ID to state key
    const stateKey = FIELD_ID_TO_STATE_KEY[name];
    if (stateKey) {
      setFormData(prev => ({ ...prev, [stateKey]: value }));
      
      // Clear error when user starts typing
      if (errors[stateKey]) {
        setErrors(prev => ({ ...prev, [stateKey]: undefined }));
      }
    }
  };

  return (
    <section id="contact" className="py-20 md:py-32 bg-background theme-transition">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          variants={fadeInUp}
          className="mb-12 md:mb-20"
        >
          <div className="flex items-center gap-3 mb-4">
            <Shield className="w-5 h-5 text-primary" />
            <span className="font-inter font-medium text-sm text-primary">
              {t('contact.secureSubmission')}
            </span>
          </div>
          <h2 className="font-inter font-bold text-3xl sm:text-4xl md:text-5xl text-foreground mb-6">
            {t('contact.title')}
          </h2>
          <p className="font-inter font-normal text-base md:text-lg text-foreground/80 max-w-3xl">
            {t('contact.description')}
          </p>
        </motion.div>

        <div className="max-w-2xl mx-auto">
          {/* Success Message */}
          {isSuccess && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-6 border border-[#22c55e]/20 bg-[#22c55e]/10 rounded-none"
            >
              <div className="flex items-center">
                <CheckCircle className="w-5 h-5 text-[#22c55e] mr-3" />
                <div>
                  <h3 className="font-inter font-bold text-lg text-foreground mb-1">
                    {t('contact.successTitle')}
                  </h3>
                  <p className="font-inter font-normal text-sm text-foreground/90">
                    {t('contact.successMessage')}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Spam Protection Error */}
          {spamError && (
            <motion.div
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-6 border border-destructive/20 bg-destructive/10 rounded-none"
            >
              <div className="flex items-center">
                <AlertCircle className="w-5 h-5 text-destructive mr-3" />
                <div>
                  <h3 className="font-inter font-bold text-lg text-foreground mb-1">
                    Spam Protection
                  </h3>
                  <p className="font-inter font-normal text-sm text-foreground/90">
                    {spamError}
                  </p>
                </div>
              </div>
            </motion.div>
          )}

          {/* Form */}
          <motion.form
            ref={formRef}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            onSubmit={handleSubmit}
            action={GOOGLE_FORM_ACTION}
            method="POST"
            target="hidden_iframe"
            className="space-y-8"
          >
            {/* Honeypot Field - Hidden from users */}
            <input
              type="text"
              name="_gotcha"
              className="hidden"
              aria-hidden="true"
              tabIndex={-1}
              autoComplete="off"
              value={honeypot}
              onChange={(e) => setHoneypot(e.target.value)}
            />

            {/* Name Field */}
            <div>
              <label
                htmlFor="name"
                className="block font-inter font-medium text-sm text-foreground mb-3"
              >
                {t('contact.name')}
              </label>
              <input
                type="text"
                id="name"
                name={GOOGLE_FORM_FIELDS.name}
                value={formData.name}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-transparent border font-inter font-normal text-base text-foreground placeholder:text-foreground/80 rounded-none focus:outline-none focus:ring-1 focus:ring-primary transition-all ${
                  errors.name
                    ? "border-destructive focus:border-destructive"
                    : "border-border focus:border-primary"
                }`}
                placeholder={t('contact.namePlaceholder')}
                aria-describedby={errors.name ? "name-error" : undefined}
                aria-invalid={!!errors.name}
                disabled={isSubmitting}
                required
              />
              {errors.name && (
                <div id="name-error" className="flex items-center mt-2">
                  <AlertCircle className="w-4 h-4 text-destructive mr-2" />
                  <span className="font-inter font-normal text-sm text-destructive">
                    {errors.name}
                  </span>
                </div>
              )}
            </div>

            {/* Email Field */}
            <div>
              <label
                htmlFor="email"
                className="block font-inter font-medium text-sm text-foreground mb-3"
              >
                {t('contact.email')}
              </label>
              <input
                type="email"
                id="email"
                name={GOOGLE_FORM_FIELDS.email}
                value={formData.email}
                onChange={handleChange}
                className={`w-full px-4 py-3 bg-transparent border font-inter font-normal text-base text-foreground placeholder:text-foreground/80 rounded-none focus:outline-none focus:ring-1 focus:ring-primary transition-all ${
                  errors.email
                    ? "border-destructive focus:border-destructive"
                    : "border-border focus:border-primary"
                }`}
                placeholder={t('contact.emailPlaceholder')}
                aria-describedby={errors.email ? "email-error" : undefined}
                aria-invalid={!!errors.email}
                disabled={isSubmitting}
                required
              />
              {errors.email && (
                <div id="email-error" className="flex items-center mt-2">
                  <AlertCircle className="w-4 h-4 text-destructive mr-2" />
                  <span className="font-inter font-normal text-sm text-destructive">
                    {errors.email}
                  </span>
                </div>
              )}
            </div>

            {/* Brief Field */}
            <div>
              <label
                htmlFor="brief"
                className="block font-inter font-medium text-sm text-foreground mb-3"
              >
                {t('contact.brief')}
              </label>
              <textarea
                id="brief"
                name={GOOGLE_FORM_FIELDS.brief}
                value={formData.brief}
                onChange={handleChange}
                rows={6}
                className={`w-full px-4 py-3 bg-transparent border font-inter font-normal text-base text-foreground placeholder:text-foreground/80 rounded-none focus:outline-none focus:ring-1 focus:ring-primary transition-all resize-none ${
                  errors.brief
                    ? "border-destructive focus:border-destructive"
                    : "border-border focus:border-primary"
                }`}
                placeholder={t('contact.briefPlaceholder')}
                aria-describedby={errors.brief ? "brief-error" : undefined}
                aria-invalid={!!errors.brief}
                disabled={isSubmitting}
                required
              />
              <div className="flex justify-between items-center mt-2">
                {errors.brief ? (
                  <div id="brief-error" className="flex items-center">
                    <AlertCircle className="w-4 h-4 text-destructive mr-2" />
                    <span className="font-inter font-normal text-sm text-destructive">
                      {errors.brief}
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4 text-foreground/80" />
                    <span className="font-inter font-normal text-sm text-foreground/80">
                      {t('contact.minCharacters')}
                    </span>
                  </div>
                )}
                <div className="font-inter font-normal text-sm text-foreground/80">
                  {formData.brief.length}/2000 characters
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <div className="pt-4">
              <motion.button
                type="submit"
                disabled={isSubmitting}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className={`w-full flex items-center justify-center px-8 py-4 font-inter font-bold text-base rounded-none border transition-all min-h-[44px] ${
                  isSubmitting
                    ? "bg-primary/50 text-primary-foreground border-primary/50 cursor-not-allowed"
                    : "bg-primary text-primary-foreground border-primary hover:bg-transparent hover:text-primary"
                }`}
                aria-label={isSubmitting ? t('contact.submitting') : t('contact.submit')}
              >
                {isSubmitting ? (
                  <>
                    <div className="w-5 h-5 border-2 border-primary-foreground border-t-transparent rounded-full animate-spin mr-3" />
                    {t('contact.submitting')}
                  </>
                ) : (
                  <>
                    <Send className="w-5 h-5 mr-3" />
                    {t('contact.submit')}
                  </>
                )}
              </motion.button>
              
              <div className="flex items-center justify-center gap-4 mt-4">
                <div className="flex items-center gap-2">
                  <Shield className="w-4 h-4 text-foreground/80" />
                  <span className="font-inter font-normal text-sm text-foreground/80">
                    {t('contact.secureForm')}
                  </span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-foreground/80" />
                  <span className="font-inter font-normal text-sm text-foreground/80">
                    {t('contact.quickResponse')}
                  </span>
                </div>
              </div>
              
              <p className="font-inter font-normal text-sm text-foreground/80 text-center mt-4">
                {t('contact.privacyNotice')}
              </p>
            </div>
          </motion.form>

          {/* Hidden Iframe for Google Forms submission */}
          <iframe
            ref={iframeRef}
            name="hidden_iframe"
            id="hidden_iframe"
            title="Form Submission Target"
            className="hidden"
            onLoad={handleIframeLoad}
          />

          {/* Alternative Contact */}
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="mt-12 pt-8 border-t border-border"
          >
            <h3 className="font-inter font-bold text-lg text-foreground mb-4">
              {t('contact.directContact')}
            </h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <p className="font-inter font-medium text-sm text-foreground mb-2">
                  {t('contact.emailLabel')}
                </p>
                <a
                  href="mailto:contact@dumitrescuphotography.com"
                  className="font-inter font-normal text-base text-primary hover:underline transition-all"
                >
                  contact@dumitrescuphotography.com
                </a>
              </div>
              <div>
                <p className="font-inter font-medium text-sm text-foreground mb-2">
                  Message me on social media
                </p>
                <div className="flex gap-4">
                  <a
                    href="https://facebook.com/dumitrescustudio"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary hover:underline transition-all min-h-[44px]"
                  >
                    <Facebook className="w-4 h-4 mr-2" />
                    <span className="font-inter font-normal text-base">Facebook</span>
                  </a>
                  <a
                    href="https://instagram.com/dumitrescustudio"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center text-primary hover:underline transition-all min-h-[44px]"
                  >
                    <Instagram className="w-4 h-4 mr-2" />
                    <span className="font-inter font-normal text-base">Instagram</span>
                  </a>
                </div>
              </div>
            </div>
            <div className="mt-4">
              <p className="font-inter font-medium text-sm text-foreground mb-2">
                {t('contact.responseTime')}
              </p>
              <p className="font-inter font-normal text-base text-foreground/80">
                {t('contact.hoursResponse')}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default Contact;